import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Toaster } from 'sonner';
import '@/i18n';
import '@/App.css';

// Pages
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import MainDashboard from '@/pages/MainDashboard';

// Admin Pages
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminEmployees from '@/pages/admin/Employees';
import AdminAttendance from '@/pages/admin/Attendance';
import AdminLeaves from '@/pages/admin/Leaves';
import OpeningBalance from '@/pages/admin/OpeningBalance';
import Custody from '@/pages/admin/Custody';
import AdminPayroll from '@/pages/admin/Payroll';
import AdminReports from '@/pages/admin/Reports';
import AdminProfile from '@/pages/admin/Profile';
import AdminSettings from '@/pages/admin/Settings';
import InvestigationsAdmin from '@/pages/admin/Investigations';
import EmployeeInvestigation from '@/pages/employee/Investigation';
import AdminNotifications from '@/pages/admin/Notifications';
import AdminQiwa from '@/pages/admin/Qiwa';
import AdminGovernment from '@/pages/admin/Government';
import AdminClearance from '@/pages/admin/Clearance';
import AdminIncidents from '@/pages/admin/Incidents';
import AdminSurveys from '@/pages/admin/Surveys';
import AdminSurveyResults from '@/pages/admin/SurveyResults';

// Recruitment Pages
import RecruitmentDashboard from '@/pages/recruitment/Dashboard';
import DeptRecruitment from '@/pages/admin/DeptRecruitment';
import GMRecruitment from '@/pages/admin/GMRecruitment';

// Employee Pages
import EmployeeDashboard from '@/pages/employee/Dashboard';
import EmployeeAttendance from '@/pages/employee/Attendance';
import EmployeeLeaves from '@/pages/employee/Leaves';
import EmployeePayslips from '@/pages/employee/Payslips';
import EmployeeProfile from '@/pages/employee/Profile';
import EmployeeRequests from '@/pages/employee/Requests';
import EmployeeAttendanceCorrections from '@/pages/employee/AttendanceCorrections';
import EmployeeMyDocuments from '@/pages/employee/MyDocuments';
import EmployeeIncidents from '@/pages/employee/Incidents';
import MySurveys from '@/pages/employee/MySurveys';
import SurveyFill from '@/pages/employee/SurveyFill';
import AdminRequests from '@/pages/admin/Requests';
import AdminPushSubscribers from '@/pages/admin/PushSubscribers';
import AdminAttendanceCorrections from '@/pages/admin/AttendanceCorrections';
import AdminReturnToWork from '@/pages/admin/ReturnToWork';
import SecurityAudit from '@/pages/admin/SecurityAudit';
import OrgChart from '@/pages/admin/OrgChart';
import LanguageSync from '@/components/LanguageSync';
import { registerServiceWorker, subscribeToPush } from '@/utils/pwa';
import LucentLoader from '@/components/LucentLoader';
import ForceChangePassword from '@/pages/ForceChangePassword';
import PrivacyPolicyGuard from '@/components/PrivacyPolicyGuard';
import VerifyLetter from '@/pages/VerifyLetter';
import FloatingHRWidget from '@/components/FloatingHRWidget';
import BottomNav from '@/components/BottomNav';

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false, allowManagers = false, allowFinance = false, allowIT = false, allowGovernmentOfficer = false }) {
  const { user, loading, isAdmin, setUser } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LucentLoader />;
  }

  if (!user) {
    // Preserve the originally requested path so deep links from emails
    // (e.g. "Open Survey" button) land on the right page after login.
    const next = `${location.pathname}${location.search || ''}`;
    const query = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/login${query}`} replace />;
  }

  if (user.must_change_password) {
    return <ForceChangePassword onSuccess={() => setUser({ ...user, must_change_password: false })} />;
  }

  // Permission gate. `requireAdmin` historically permits Admin + GM.
  // `allowManagers` widens that gate to include department & finance
  // managers — used by approval-centric pages (leaves, clearances) that
  // are also part of a department manager's workflow.
  // `allowFinance` widens the gate to include the Finance Manager ONLY
  // (NOT department managers) — used by payroll-adjacent pages like
  // Reports where finance needs the full-company picture.
  // `allowIT` widens the gate to include the IT manager (fixed email)
  // when they need to view forwarded id_card requests.
  // `allowGovernmentOfficer` widens the gate for the scoped
  // Government Affairs delegate — a regular employee who was granted
  // create/update rights inside the Government module ONLY.
  const isPrivileged =
    isAdmin ||
    user?.role === 'general_manager' ||
    !!user?.is_general_manager ||
    (allowManagers && (user?.is_department_manager || user?.is_finance_manager)) ||
    (allowFinance && !!user?.is_finance_manager) ||
    (allowIT && !!user?.is_it_manager) ||
    (allowGovernmentOfficer && !!user?.is_government_officer);

  if (requireAdmin && !isPrivileged) {
    return <Navigate to="/home" replace />;
  }

  return children;
}

// Redirect based on role
function RoleBasedRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LucentLoader />;
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return <Navigate to="/" replace />;
}

function App() {
  React.useEffect(() => {
    // Register the PWA service worker as early as possible so push
    // notifications can wake it even while the tab is closed.
    registerServiceWorker().then((reg) => {
      if (!reg) return;
      // If the user previously granted notification permission, re-sync
      // the subscription with the backend on every load. This keeps a
      // fresh endpoint after long absences.
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        subscribeToPush();
      }
    });
  }, []);

  return (
    <AuthProvider>
      <LanguageProvider>
        <ThemeProvider>
        <LanguageSync />
        <PrivacyPolicyGuard>
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify" element={<VerifyLetter />} />
            <Route path="/verify/:docNumber" element={<VerifyLetter />} />
            <Route path="/recruitment" element={<ProtectedRoute requireAdmin><RecruitmentDashboard /></ProtectedRoute>} />
            <Route path="/admin/dept-recruitment" element={<ProtectedRoute requireAdmin allowManagers><DeptRecruitment /></ProtectedRoute>} />
            <Route path="/admin/gm-recruitment" element={<ProtectedRoute requireAdmin allowManagers><GMRecruitment /></ProtectedRoute>} />

            {/* Main Dashboard - Grid Layout */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <MainDashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin Sub-pages */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/employees"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminEmployees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/attendance"
              element={
                <ProtectedRoute requireAdmin allowManagers>
                  <AdminAttendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/leaves"
              element={
                <ProtectedRoute requireAdmin allowManagers>
                  <AdminLeaves />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/opening-balance"
              element={
                <ProtectedRoute requireAdmin>
                  <OpeningBalance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/custody"
              element={
                <ProtectedRoute requireAdmin>
                  <Custody />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/incidents"
              element={
                <ProtectedRoute requireAdmin allowManagers>
                  <AdminIncidents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/incidents"
              element={
                <ProtectedRoute>
                  <EmployeeIncidents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/surveys"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSurveys />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/surveys/:id/results"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSurveyResults />
                </ProtectedRoute>
              }
            />
            <Route
              path="/surveys"
              element={
                <ProtectedRoute>
                  <MySurveys />
                </ProtectedRoute>
              }
            />
            <Route
              path="/surveys/:id"
              element={
                <ProtectedRoute>
                  <SurveyFill />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payroll"
              element={
                <ProtectedRoute requireAdmin allowManagers>
                  <AdminPayroll />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute requireAdmin allowFinance>
                  <AdminReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/profile"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/investigations"
              element={
                <ProtectedRoute requireAdmin>
                  <InvestigationsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/investigations"
              element={
                <ProtectedRoute>
                  <EmployeeInvestigation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/investigations/:id"
              element={
                <ProtectedRoute>
                  <EmployeeInvestigation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/push-subscribers"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPushSubscribers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/qiwa"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminQiwa />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/government"
              element={
                <ProtectedRoute requireAdmin allowGovernmentOfficer>
                  <AdminGovernment />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/clearance"
              element={
                <ProtectedRoute>
                  <AdminClearance />
                </ProtectedRoute>
              }
            />

            {/* Employee Sub-pages */}
            <Route
              path="/employee/dashboard"
              element={
                <ProtectedRoute>
                  <EmployeeDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/attendance"
              element={
                <ProtectedRoute>
                  <EmployeeAttendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/leaves"
              element={
                <ProtectedRoute>
                  <EmployeeLeaves />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/payslips"
              element={
                <ProtectedRoute>
                  <EmployeePayslips />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/profile"
              element={
                <ProtectedRoute>
                  <EmployeeProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/requests"
              element={
                <ProtectedRoute>
                  <EmployeeRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/requests"
              element={
                <ProtectedRoute requireAdmin allowIT>
                  <AdminRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/attendance-corrections"
              element={
                <ProtectedRoute>
                  <EmployeeAttendanceCorrections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/my-documents"
              element={
                <ProtectedRoute>
                  <EmployeeMyDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/attendance-corrections"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminAttendanceCorrections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/return-to-work"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminReturnToWork />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/security-audit"
              element={
                <ProtectedRoute requireAdmin>
                  <SecurityAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/org-chart"
              element={
                <ProtectedRoute requireAdmin>
                  <OrgChart />
                </ProtectedRoute>
              }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <FloatingHRWidget />
          <BottomNav />
        </BrowserRouter>
        </PrivacyPolicyGuard>
        <Toaster position="top-center" richColors dir="auto" />
        </ThemeProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
